// Refresh build comment: 2026-06-03 - Forced Cache Clear v5 - REBUILD
import { getEnvironment } from "@/utils/previewCacheGuard";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import GlobalCrmNotifications from "@/components/crm/GlobalCrmNotifications";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { StoreAuthProvider } from "@/context/StoreAuthContext";
import { SessionContextProvider } from "@/context/SessionContextProvider";
import { SupabaseAuthProvider } from "@/hooks/useSupabaseAuth";
import { DynamicSlashItemsBridge } from "@/admin/hooks/DynamicSlashItemsBridge";
import { useStoreAuth } from "@/context/StoreAuthContext";
import { useEffect, lazy, Suspense } from "react";
import { useUserType } from "@/hooks/useUserType";
import { ContextRouteGuard } from "@/components/context/ContextRouteGuard";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { ErrorAlertProvider } from "@/components/ui/error-alert-provider";
import { useInjectBrandingIntoSplash } from "@/hooks/useInjectBrandingIntoSplash";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";
import { useIosStandaloneViewportFix } from "@/hooks/useIosStandaloneViewportFix";
import { PwaUpdatePrompt } from "@/components/pwa/PwaUpdatePrompt";
import { InstallInvite } from "@/components/pwa/InstallInvite";
import { MobileInstallFlow } from "@/components/pwa/MobileInstallFlow";
import PushPermissionToast from "@/components/notifications/PushPermissionToast";
import WhatsAppPhoneInvite from "@/components/comm/WhatsAppPhoneInvite";
import AnnouncementModalHost from "@/components/announcements/AnnouncementModalHost";
import { ConnectionStatusBanner } from "@/components/pwa/ConnectionStatusBanner";
import { useBrokerAccess } from "@/hooks/useBrokerAccess";
import { getEnvironmentChoice } from '@/lib/environmentChoice';
import ChooseEnvironment from '@/pages/ChooseEnvironment';

import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import SelectContextPage from "./pages/context/SelectContextPage";
import Diagnostics from "./pages/Diagnostics";
import DiagnosticFloatingButton from "./components/DiagnosticFloatingButton";

import { configureRoutePreloading } from "@/utils/preloadRoutes";
import { AdminAuthProvider } from "@/admin/context/AdminAuthContext";
import { supabase } from '@/integrations/supabase/client';
import { createLazyPage } from '@/components/routing/LazyPageLoader';

// Lazy load com retry automático e auto-refresh
const DesignSystem = createLazyPage(() => import('./pages/design-system/DesignSystem'));
const LazyPublicSiteRouter = createLazyPage(() => import('./pages/publicSite/PublicSiteRouter'));
const LazyCopa2026Router = createLazyPage(() => import('./pages/copa2026/Copa2026Router'));
const LazyAdminIndex = createLazyPage(() => import('./admin/pages/AdminIndex'));


// StoreIndex com FinancialProvider lazy-loaded (movido do App.tsx)
const LazyStoreIndex = createLazyPage(() => import('./pages/store/StoreIndexWithFinancial'));
const LazyCrmIndex = createLazyPage(() => import('./pages/crm/CrmIndex'));
const LazyVitrineIndex = createLazyPage(() => import('./pages/vitrine/VitrineIndex'));
const LazyBrokerPortal = createLazyPage(() => import('./pages/broker/BrokerPortal'));
const LazyBrokerVehicleDetails = createLazyPage(() => import('./pages/broker/BrokerVehicleDetails'));
const LazyBrokerProfile = createLazyPage(() => import('./pages/broker/BrokerProfile'));
const LazyBrokerNotifications = createLazyPage(() => import('./pages/broker/BrokerNotifications'));
const LazyBrokerRegistration = createLazyPage(() => import('./pages/broker/BrokerRegistration'));
const LazyBrokerFirstAccess = createLazyPage(() => import('./pages/broker/BrokerFirstAccess'));

const LazySimuladorIndex = createLazyPage(() => import('./pages/simulador/SimuladorIndex'));
const LazyPrivacyPolicy = createLazyPage(() => import('./pages/PrivacyPolicy'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache somente em memória para evitar reidratação de telas antigas após deploy.
      gcTime: 1000 * 60 * 60 * 24, // 24h em memória
    },
  },
});


// Componente para injetar branding no splash (deve estar dentro do QueryClientProvider)
const BrandingInjector = () => {
  useInjectBrandingIntoSplash();
  return null;
};

// Navigation Logger Component
const NavigationLogger = () => {
  const location = useLocation();
  
  useEffect(() => {
    console.log('Navegação para:', location.pathname, location.search, location.hash);
  }, [location]);
  
  return null;
};

// Store Protected Route - usa splash HTML unificado (não renderiza nada durante loading)
const StoreProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, status, retry } = useStoreAuth();
  const { isSuperAdmin, isLoading: typeLoading } = useUserType();
  const { isBrokerOnly, hasBrokerAccess, isLoading: brokerLoading } = useBrokerAccess();
  const isBooting = status === 'booting' || typeLoading || brokerLoading;
  
  useEffect(() => {
    if (!isBooting) {
      if (status === 'error') {
        console.error('🛡️ StoreProtectedRoute - AUTH ERROR: Infrastructure failure');
      } else if (!user) {
        console.log('🛡️ StoreProtectedRoute - AUTH CONFIRMED: No user, redirecting to /login');
      } else {
        console.log('🛡️ StoreProtectedRoute - AUTH CONFIRMED: User authenticated:', user.email);
      }
    }
  }, [isBooting, user, status]);

  if (isBooting) {
    console.log('🛡️ StoreProtectedRoute - BOOTING: Waiting for auth state...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground animate-pulse">Iniciando sistema...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 text-center">
        <h2 className="text-xl font-semibold mb-2">Não foi possível validar sua sessão</h2>
        <p className="text-muted-foreground mb-6 max-w-md">
          Ocorreu uma falha temporária na conexão. Por favor, tente novamente.
        </p>
        <button 
          onClick={() => retry()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Se for Super Admin, redirecionar para área admin
  if (isSuperAdmin) {
    console.log('🛡️ StoreProtectedRoute - SUPERADMIN: Redirecting to /super-admin');
    return <Navigate to="/super-admin" replace />;
  }

  if (isBrokerOnly) {
    return <Navigate to="/portal-corretor" replace />;
  }
  if (hasBrokerAccess && !getEnvironmentChoice(user.id)) {
    return <Navigate to="/escolher-ambiente" replace />;
  }
  
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

const BrokerProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, status, retry } = useStoreAuth();
  const { hasBrokerAccess, isBrokerOnly, isLoading } = useBrokerAccess();

  if (status === 'booting' || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <p className="text-muted-foreground">Não foi possível validar sua sessão.</p>
        <button onClick={() => retry()} className="px-4 py-2 bg-primary text-primary-foreground rounded-md">Tentar novamente</button>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!hasBrokerAccess) return <Navigate to="/inicio" replace />;
  if (!isBrokerOnly && !getEnvironmentChoice(user.id)) return <Navigate to="/escolher-ambiente" replace />;
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// Super Admin Protected Route
const SuperAdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, status, retry } = useStoreAuth();
  const { isSuperAdmin, isRegularUser, isLoading: typeLoading } = useUserType();
  
  const isBooting = status === 'booting' || typeLoading;
  
  if (isBooting) {
    console.log('🛡️ SuperAdminProtectedRoute - BOOTING: Waiting for auth state...');
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm">Iniciando sistema...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 text-center">
        <h2 className="text-xl font-semibold mb-2">Erro de conexão</h2>
        <button 
          onClick={() => retry()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  
  // Se não estiver autenticado (confirmado), redirecionar para login
  if (!user) {
    console.log('🛡️ SuperAdminProtectedRoute - AUTH CONFIRMED: No user, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Se for usuário comum (confirmado), redirecionar para área de usuário
  if (isRegularUser && !isSuperAdmin) {
    console.log('🛡️ SuperAdminProtectedRoute - REGULAR USER: Redirecting to /inicio');
    return <Navigate to="/inicio" replace />;
  }
  
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

const App = () => {
  useAppVersionCheck();
  useIosStandaloneViewportFix();
  const env = getEnvironment();
  const isPreview = env === 'preview' || env === 'development';


  useEffect(() => {
    console.log('%c [App] V4_FIX_DATES_FINAL - Loaded ', 'background: #22c55e; color: #fff; font-weight: bold;');


    // Configurar preloading de rotas
    configureRoutePreloading();
    
    // Add global error handler for unhandled navigation errors
    const handleError = (event: ErrorEvent) => {
      // Suppress Turnstile cleanup errors to prevent app crash
      const msg = event.error?.message || event.message || '';
      if (msg.includes('turnstile') && msg.includes('remove')) {
        console.warn('[App] Erro do Turnstile suprimido:', msg);
        event.preventDefault();
        return;
      }
      console.error('Erro global capturado:', event.error);
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = String(event.reason || '');
      if (reason.includes('turnstile') && reason.includes('remove')) {
        console.warn('[App] Rejeição do Turnstile suprimida:', reason);
        event.preventDefault();
        return;
      }
      console.error('Promise rejeitada não tratada:', event.reason);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  console.log('App renderizado - iniciando aplicação');
  
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingInjector />
      <ThemeProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <ErrorAlertProvider>
              <SupabaseAuthProvider>
                <SessionContextProvider>
                  <StoreAuthProvider>
                  <DynamicSlashItemsBridge />
                  <Toaster />
                  <Sonner />
                  <PwaUpdatePrompt />
                  <ConnectionStatusBanner />

                  <BrowserRouter>
                    <NavigationLogger />
                    <ContextRouteGuard />
                    <InstallInvite />
                    <MobileInstallFlow />
                    <AnnouncementModalHost />
                    <GlobalCrmNotifications />
                    <PushPermissionToast />
                    <WhatsAppPhoneInvite />
                    {isPreview && <DiagnosticFloatingButton />}
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/escolher-ambiente" element={<ChooseEnvironment />} />
                      <Route path="/esqueci-senha" element={<ForgotPassword />} />
                      <Route path="/redefinir-senha" element={<ResetPassword />} />
                      <Route path="/cadastro-corretor/:slug" element={<LazyBrokerRegistration />} />
                      <Route path="/corretor/primeiro-acesso" element={<LazyBrokerFirstAccess />} />
                      <Route path="/politica-de-privacidade" element={<LazyPrivacyPolicy />} />
                      
                      {/* Redirecionar /select-context obsoleto para /inicio */}
                      <Route path="/select-context" element={<Navigate to="/inicio" replace />} />


                      
                      {/* 👑 SUPER ADMIN - Platform Management (ANTES do catch-all) */}
                      <Route path="/super-admin/*" element={
                        <SuperAdminProtectedRoute>
                          <AdminAuthProvider>
                            <LazyAdminIndex />
                          </AdminAuthProvider>
                        </SuperAdminProtectedRoute>
                      } />
                      
                      {/* Design System - standalone route */}
                      <Route path="/design-system" element={
                        <ErrorBoundary>
                          <DesignSystem />
                        </ErrorBoundary>
                      } />
                      
                      {/* Diagnostic Page */}
                      <Route path="/diagnostico" element={<Diagnostics />} />
                      
                      
                      
                      {/* 🌐 PUBLIC SITE - No Auth Required */}
                      <Route path="/site-publico/*" element={
                        <ErrorBoundary>
                          <LazyPublicSiteRouter />
                        </ErrorBoundary>
                      } />

                      {/* ⚽ Copa do Mundo 2026 - rota pública isolada */}
                      <Route path="/copa-2026/*" element={
                        <ErrorBoundary>
                          <LazyCopa2026Router />
                        </ErrorBoundary>
                      } />

                      
                      {/* CRM Module - layout dedicado dentro do AutoFluxo */}
                      <Route path="/crm/*" element={
                        <StoreProtectedRoute>
                          <LazyCrmIndex />
                        </StoreProtectedRoute>
                      } />

                      {/* Simulador - módulo dedicado (mesmos componentes) */}
                      <Route path="/simulador/*" element={
                        <StoreProtectedRoute>
                          <LazySimuladorIndex />
                        </StoreProtectedRoute>
                      } />

                      {/* Vitrine Module - Layout dedicado */}
                      <Route path="/vitrine/*" element={
                        <StoreProtectedRoute>
                          <LazyVitrineIndex />
                        </StoreProtectedRoute>
                      } />

                      <Route path="/portal-corretor" element={
                        <BrokerProtectedRoute>
                          <LazyBrokerPortal />
                        </BrokerProtectedRoute>
                      } />
                      <Route path="/portal-corretor/veiculo/:id" element={
                        <BrokerProtectedRoute>
                          <LazyBrokerVehicleDetails />
                        </BrokerProtectedRoute>
                      } />
                      <Route path="/portal-corretor/notificacoes" element={
                        <BrokerProtectedRoute>
                          <LazyBrokerNotifications />
                        </BrokerProtectedRoute>
                      } />
                      <Route path="/portal-corretor/perfil" element={
                        <BrokerProtectedRoute>
                          <LazyBrokerProfile />
                        </BrokerProtectedRoute>
                      } />

                      {/* A raiz abre a entrada do AutoFluxo. */}
                      <Route path="/" element={<Navigate to="/inicio" replace />} />

                      {/* Store routes (catch-all) - FinancialProvider lazy-loaded */}
                      <Route path="/*" element={
                        <StoreProtectedRoute>
                          <LazyStoreIndex />
                        </StoreProtectedRoute>
                      } />
                    </Routes>
                  </BrowserRouter>
                  </StoreAuthProvider>
                </SessionContextProvider>
              </SupabaseAuthProvider>
            </ErrorAlertProvider>
          </ErrorBoundary>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
